import { useTranslation } from 'react-i18next';
import * as ExcelJS from 'exceljs';
import { format } from 'date-fns';
import { getErrorMessage } from '@/types/api';
import type { Site } from '../types';
import { useExcelFormat } from '../helpers/useExcelFormat';
import { getVatRate } from '@/config/appConfig';

export const useExcelExport = () => {
  const { t, i18n } = useTranslation();
  const { formatSitesTable, formatTableWithHeaders } = useExcelFormat();
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';

  const styleCell = (cell: ExcelJS.Cell) => {
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFC0C0C0' } },
      bottom: { style: 'thin', color: { argb: 'FFC0C0C0' } },
      left: { style: 'thin', color: { argb: 'FFC0C0C0' } },
      right: { style: 'thin', color: { argb: 'FFC0C0C0' } },
    };

    if (isRTL) {
      cell.alignment = {
        textRotation: 0,
        horizontal: 'right',
      };
    }
  };

  const exportToExcel = async (
    sites: Site[],
    projectName: string,
    projectDiscount: number = 0,
    onComplete: () => void
  ) => {
    try {
      const workbook = new ExcelJS.Workbook();
      const sheetName = t('sites_export_sheet_name', 'Sites Report');

      const worksheet = workbook.addWorksheet(sheetName, {
        views: [
          {
            state: 'frozen',
            xSplit: 0,
            ySplit: 1,
            topLeftCell: 'A2',
            activeCell: 'A2',
            rightToLeft: isRTL,
          },
        ],
      });

      // Prepare data
      const siteData = formatSitesTable(sites);
      const formattedData = formatTableWithHeaders(siteData);

      // Set up columns
      const headerNames = Object.keys(formattedData[0] || {});

      worksheet.columns = headerNames.map(header => ({
        header,
        key: header,
      }));

      // Set column widths
      worksheet.columns.forEach(column => {
        if (column.header) {
          column.width = column.header.length < 16 ? 16 : column.header.length;
        }
      });

      // Format header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '0a3e90' },
      };

      headerNames.forEach((header, colIndex) => {
        const colLetter = String.fromCharCode(65 + colIndex);
        const cell = worksheet.getCell(`${colLetter}1`);
        cell.value = header;
        cell.alignment = { horizontal: 'center' };
        styleCell(cell);
      });

      // Add data rows
      formattedData.forEach((row, index) => {
        const rowIndex = index + 2;
        Object.keys(row).forEach((key, colIndex) => {
          const colLetter = String.fromCharCode(65 + colIndex);
          const cell = worksheet.getCell(`${colLetter}${rowIndex}`);

          // Special formatting for price column (index 3)
          if (colIndex === 3) {
            const cleanValue = (row[key] as string).replace(/,/g, '');
            cell.numFmt = '#,##0.00';
            cell.value = Math.round(parseFloat(cleanValue) * 100) / 100;
          } else {
            cell.value = row[key];
          }

          styleCell(cell);
        });
      });

      // Add summary section with multiple rows
      let currentRowIndex = formattedData.length + 2;
      const dataEndRow = formattedData.length + 1; // Last row with data

      // Row 1: Total (סך הכל)
      const totalLabelCell = worksheet.getCell(`C${currentRowIndex}`);
      totalLabelCell.value = t('total_table_exported_file', 'סך הכל');
      styleCell(totalLabelCell);

      const totalValueCell = worksheet.getCell(`D${currentRowIndex}`);
      totalValueCell.numFmt = '#,##0.00';
      // Use SUM formula instead of hardcoded value
      totalValueCell.value = { formula: `SUM(D2:D${dataEndRow})` };
      styleCell(totalValueCell);

      currentRowIndex++;

      // Row 2: Discount (הנחה)
      const discountLabelCell = worksheet.getCell(`C${currentRowIndex}`);
      discountLabelCell.value = t('discount_table_exported_file', 'הנחה');
      styleCell(discountLabelCell);

      const discountValueCell = worksheet.getCell(`D${currentRowIndex}`);
      discountValueCell.value = projectDiscount;
      discountValueCell.numFmt = '0"%"';
      discountValueCell.alignment = { horizontal: 'right' };
      styleCell(discountValueCell);

      const discountRowIndex = currentRowIndex; // Store discount row index

      currentRowIndex++;

      // Row 3: Total after discount (סה"כ אחרי הנחה)
      const totalAfterDiscountLabelCell = worksheet.getCell(
        `C${currentRowIndex}`
      );
      totalAfterDiscountLabelCell.value = t(
        'total_after_discount_table_exported_file',
        'Total after discount'
      );
      styleCell(totalAfterDiscountLabelCell);

      const totalAfterDiscountValueCell = worksheet.getCell(
        `D${currentRowIndex}`
      );
      totalAfterDiscountValueCell.numFmt = '#,##0.00';
      // Calculate total after discount using formula: Total - (Total * Discount%)
      const totalRowIndex = currentRowIndex - 2; // Reference to the total row
      totalAfterDiscountValueCell.value = {
        formula: `D${totalRowIndex}*(1-D${discountRowIndex}/100)`,
      };
      styleCell(totalAfterDiscountValueCell);

      currentRowIndex++;

      // Row 4: VAT (מע"מ)
      const vatLabelCell = worksheet.getCell(`C${currentRowIndex}`);
      vatLabelCell.value = t('vat_table_exported_file', 'מע"מ');
      styleCell(vatLabelCell);

      const vatValueCell = worksheet.getCell(`D${currentRowIndex}`);
      vatValueCell.value = getVatRate();
      vatValueCell.numFmt = '0"%"';
      vatValueCell.alignment = { horizontal: 'right' };
      styleCell(vatValueCell);

      const vatRowIndex = currentRowIndex; // Store VAT row index

      currentRowIndex++;

      // Row 5: Grand total with VAT (סה"כ כולל מע"מ)
      const grandTotalLabelCell = worksheet.getCell(`C${currentRowIndex}`);
      grandTotalLabelCell.value = t(
        'grand_total_table_exported_file',
        'Grand total with VAT'
      );
      styleCell(grandTotalLabelCell);

      const grandTotalValueCell = worksheet.getCell(`D${currentRowIndex}`);
      grandTotalValueCell.numFmt = '#,##0.00';
      // Calculate grand total with VAT using formula: Total after discount * (1 + VAT%)
      const totalAfterDiscountRowIndex = currentRowIndex - 2; // Reference to total after discount row
      grandTotalValueCell.value = {
        formula: `D${totalAfterDiscountRowIndex}*(1+D${vatRowIndex}/100)`,
      };
      styleCell(grandTotalValueCell);

      // Enable autofilter
      if (headerNames.length > 0) {
        const firstCellAddress = 'A1';
        const lastColumnLetter = String.fromCharCode(
          65 + headerNames.length - 1
        );
        const lastCellAddress = `${lastColumnLetter}1`;
        worksheet.autoFilter = `${firstCellAddress}:${lastCellAddress}`;
      }

      worksheet.properties.defaultRowHeight = 15;

      // Generate filename
      const currentDate = format(new Date(), 'yyyy-MM-dd_HH-mm');
      const filename = `${projectName}_sites_${currentDate}.xlsx`;

      // Export file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Create download link
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;

      // Trigger download
      document.body.appendChild(a);
      a.click();

      // Cleanup
      document.body.removeChild(a);
      window.URL.revokeObjectURL(a.href);

      onComplete();
    } catch (error) {
      getErrorMessage(error, true);
      onComplete();
      throw error;
    }
  };

  return { exportToExcel };
};
